import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSnackbar } from "notistack";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Box,
  Button,
  TextField,
  Autocomplete,
  useTheme,
  Switch,
  Stack,
  Typography,
} from "@mui/material";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../Header";

import { tokens } from "../../theme";

import baseRequest from "../../core/baseRequest";

import { TIME } from "../../utils/const";
import moment from "moment-timezone";

import pm from "../../images/pm.png";
import useSignOut from "../../hooks/useSignOut";
import { errorHandler } from "../../core/errorHandler";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

const PreventativeBaseReportPage = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [controller] = useSignOut();

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const isNonMobile = useMediaQuery("(min-width:600px)");

  const [switchState, setSwitchState] = useState(false);

  const [targets, setTargets] = useState([]);

  const [value, setValue] = useState(undefined);

  const loadPMBasePage = async () => {
    try {
      const res = await baseRequest.get("/report/maintenanceForm", {});
      if (res.data) {
        setTargets(Object.values(res.data.records?.targets));
      }
    } catch (error) {
      const { data, status } = errorHandler(error);
      switch (status) {
        case 401:
          controller.forceLogin();
          break;
        case 403:
          navigate("/noaccess");
          break;
        case 404:
          enqueueSnackbar("Something went wrong retrieving configurations!", {
            variant: "error",
          });
          break;
        case 500:
          enqueueSnackbar(
            "Something went wrong while authenticating the user!",
            {
              variant: "error",
            }
          );
          break;
        default:
          enqueueSnackbar(data, {
            variant: "error",
          });
          break;
      }
    }
  };

  useEffect(() => {
    loadPMBasePage();
  }, []);

  useEffect(() => {
    //This is for reloading home page
    const interval = setInterval(() => {
      loadPMBasePage();
    }, TIME);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = props.title || "";
  }, [props.title]);

  const inventorySchema = yup.object().shape({
    targets: yup
      .array()
      .required("Please select the targets you want to see their PM records!")
      .min(1, "Please select the targets you want to see their PM records!"),
  });

  const initialValues = {
    targets: targets.map((item) => item.target),
    date: "",
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (switchState) {
      const newWindow = window.open("/reports/custom?pm");
      newWindow.state = {
        date: values.date.startOf("month"),
        type: "pm",
        targets: values.targets,
      };
    } else {
      const newWindow = window.open("/reports/pm");
      newWindow.state = {
        targets: values.targets,
      };
    }
  };

  return (
    <Box m="0 20px">
      <Header
        title="PULL PREVENTATIVE MAINTENANCE REPORT"
        subtitle="PM Maintenance Report"
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={inventorySchema}
        onSubmit={handleSubmit}
      >
        {({
          setFieldValue,
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                "& .MuiInputBase-root::after": {
                  borderBottomColor: colors.ciboInnerGreen[500],
                },
                "& .MuiInputBase-root::before": {
                  borderBottomColor: colors.ciboInnerGreen[600],
                },
                "& .MuiFormLabel-root.Mui-focused": {
                  color: colors.ciboInnerGreen[300],
                },
              }}
            >
              <Box sx={{ gridColumn: "span 1" }} />
              <img
                style={{
                  gridColumn: "span 2",
                  margin: "auto",
                  justifyContent: "center",
                  width: "60%",
                }}
                src={pm}
              />

              <Box sx={{ gridColumn: "span 1" }} />
              <Box sx={{ gridColumn: "span 1" }} />
              <Autocomplete
                limitTags={3}
                multiple
                disableCloseOnSelect={true}
                onChange={(_, value) => {
                  setFieldValue("targets", value);
                }}
                value={values.targets}
                sx={{ gridColumn: "span 2" }}
                options={targets.map((item) => item.target)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Targets"
                    onBlur={handleBlur}
                    name="targets"
                    error={!!touched.targets && !!errors.targets}
                    helperText={touched.targets && errors.targets}
                  />
                )}
              />

              <Box sx={{ gridColumn: "span 1" }} />
              <Box sx={{ gridColumn: "span 1" }} />

              <Box sx={{ gridColumn: "span 2" }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ justifyContent: "center" }}
                >
                  <Typography>Current Month</Typography>
                  <Switch
                    sx={{
                      width: 58,
                      height: 34,
                      padding: 1.2,
                      "& .MuiSwitch-switchBase": {
                        padding: 0,
                        margin: 0.5,
                      },
                      "& .MuiSwitch-thumb": {
                        width: 24,
                        height: 24,
                      },
                    }}
                    value={switchState}
                    color="secondary"
                    onChange={(_, switchStatus) => {
                      setSwitchState(switchStatus);
                      if (switchStatus === false) {
                        setFieldValue("date", "");
                      }
                    }}
                  />
                  <Typography>Custom</Typography>
                </Stack>
              </Box>

              <Box sx={{ gridColumn: "span 1" }} />
              <Box sx={{ gridColumn: "span 1" }} />

              <Box
                sx={{
                  gridColumn: "span 2",
                  display: switchState ? "block" : "none",
                  justifySelf: "center",
                }}
              >
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    onChange={(value) => {
                      setFieldValue("date", value);
                    }}
                    views={["month", "year"]}
                  />
                </LocalizationProvider>

                {!!touched.targets && !!errors.targets ? undefined : (
                  <Typography
                    sx={{
                      fontSize: "0.64rem",
                      color: "#d32f2f",
                      marginLeft: 1,
                      marginTop: 0.5,
                    }}
                  >
                    {touched.range && errors.range}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box display="flex" justifyContent="center" mt="20px">
              <Button type="submit" color="secondary" variant="contained">
                Pull The Report
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PreventativeBaseReportPage;
